/**
 * Delivery Notification Service
 * Handles notifications for prize delivery status changes
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore'
import { db } from '../lib/firebase'
import type {
  DeliveryNotification,
  DeliveryStatus,
  UserPrizeTracking
} from '../types/prizeDelivery'

class DeliveryNotificationService {
  private readonly COLLECTION_NAME = 'deliveryNotifications'

  /**
   * Create a notification for delivery status change
   */
  async createStatusChangeNotification(params: {
    prizeDeliveryId: string
    deliveryItemId?: string
    userId: string
    oldStatus: DeliveryStatus
    newStatus: DeliveryStatus
    trackingNumber?: string
    estimatedDeliveryDate?: Date
  }): Promise<void> {
    try {
      const { prizeDeliveryId, deliveryItemId, userId, oldStatus, newStatus, trackingNumber, estimatedDeliveryDate } = params

      // Create email notification
      const emailNotification: Omit<DeliveryNotification, 'id'> = {
        prizeDeliveryId,
        deliveryItemId,
        type: this.getNotificationType(oldStatus, newStatus),
        recipient: userId,
        method: 'email',
        status: 'pending',
        scheduledAt: new Date(),
        createdAt: new Date()
      }

      // Create push notification
      const pushNotification: Omit<DeliveryNotification, 'id'> = {
        ...emailNotification,
        method: 'push'
      }

      // Add both notifications to the queue
      await Promise.all([
        addDoc(collection(db, this.COLLECTION_NAME), {
          ...emailNotification,
          scheduledAt: serverTimestamp(),
          createdAt: serverTimestamp()
        }),
        addDoc(collection(db, this.COLLECTION_NAME), {
          ...pushNotification,
          scheduledAt: serverTimestamp(),
          createdAt: serverTimestamp()
        })
      ])

      // Send immediate notifications for critical status changes
      if (['shipped', 'delivered'].includes(newStatus)) {
        await this.sendImmediateNotification({
          userId,
          type: newStatus === 'shipped' ? 'shipped' : 'delivered',
          prizeDeliveryId,
          deliveryItemId,
          trackingNumber,
          estimatedDeliveryDate
        })
      }
    } catch (error) {
      console.error('Error creating status change notification:', error)
      throw new Error('Error creando notificación de cambio de estado')
    }
  }

  /**
   * Send immediate notification (for critical updates)
   */
  private async sendImmediateNotification(params: {
    userId: string
    type: 'shipped' | 'delivered'
    prizeDeliveryId: string
    deliveryItemId?: string
    trackingNumber?: string
    estimatedDeliveryDate?: Date
  }): Promise<void> {
    // In a real implementation, this would:
    // 1. Send push notification via Firebase Cloud Messaging
    // 2. Send email via email service (SendGrid, etc.)
    // 3. Send SMS if enabled

    console.log('Sending immediate notification:', params)

    // For now, we'll just create a notification record
    const notification: Omit<DeliveryNotification, 'id'> = {
      prizeDeliveryId: params.prizeDeliveryId,
      deliveryItemId: params.deliveryItemId,
      type: params.type,
      recipient: params.userId,
      method: 'push',
      status: 'sent',
      scheduledAt: new Date(),
      sentAt: new Date(),
      createdAt: new Date()
    }

    await addDoc(collection(db, this.COLLECTION_NAME), {
      ...notification,
      scheduledAt: serverTimestamp(),
      sentAt: serverTimestamp(),
      createdAt: serverTimestamp()
    })
  }

  /**
   * Get notification type based on status transition
   */
  private getNotificationType(oldStatus: DeliveryStatus, newStatus: DeliveryStatus): DeliveryNotification['type'] {
    if (newStatus === 'shipped') return 'shipped'
    if (newStatus === 'delivered') return 'delivered'
    if (newStatus === 'cancelled') return 'cancelled'
    if (oldStatus === 'shipped' && newStatus === 'preparing') return 'delayed'
    return 'status_change'
  }

  /**
   * Schedule delivery reminder notifications
   */
  async scheduleDeliveryReminders(prizeDeliveryId: string, deliveryItems: Array<{
    id: string
    scheduledDate?: Date
    userId: string
  }>): Promise<void> {
    try {
      const notifications: Array<Omit<DeliveryNotification, 'id'>> = []

      for (const item of deliveryItems) {
        if (!item.scheduledDate) continue

        // Schedule reminder 1 day before delivery
        const reminderDate = new Date(item.scheduledDate)
        reminderDate.setDate(reminderDate.getDate() - 1)

        notifications.push({
          prizeDeliveryId,
          deliveryItemId: item.id,
          type: 'status_change',
          recipient: item.userId,
          method: 'email',
          status: 'pending',
          scheduledAt: reminderDate,
          createdAt: new Date()
        })

        notifications.push({
          prizeDeliveryId,
          deliveryItemId: item.id,
          type: 'status_change',
          recipient: item.userId,
          method: 'push',
          status: 'pending',
          scheduledAt: reminderDate,
          createdAt: new Date()
        })
      }

      // Add all notifications
      await Promise.all(
        notifications.map(notification =>
          addDoc(collection(db, this.COLLECTION_NAME), {
            ...notification,
            scheduledAt: Timestamp.fromDate(notification.scheduledAt),
            createdAt: serverTimestamp()
          })
        )
      )
    } catch (error) {
      console.error('Error scheduling delivery reminders:', error)
      throw new Error('Error programando recordatorios de entrega')
    }
  }

  /**
   * Mark notification as sent
   */
  async markNotificationSent(notificationId: string, success: boolean, failureReason?: string): Promise<void> {
    try {
      const notificationRef = doc(db, this.COLLECTION_NAME, notificationId)

      const updateData: any = {
        status: success ? 'sent' : 'failed',
        sentAt: serverTimestamp()
      }

      if (!success && failureReason) {
        updateData.failureReason = failureReason
      }

      await updateDoc(notificationRef, updateData)
    } catch (error) {
      console.error('Error marking notification as sent:', error)
      throw new Error('Error marcando notificación como enviada')
    }
  }

  /**
   * Subscribe to pending notifications for processing
   */
  subscribeToPendingNotifications(callback: (notifications: DeliveryNotification[]) => void): () => void {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('status', '==', 'pending'),
      where('scheduledAt', '<=', new Date()),
      orderBy('scheduledAt', 'asc')
    )

    return onSnapshot(q, (snapshot) => {
      const notifications: DeliveryNotification[] = snapshot.docs.map(doc => ({
        id: doc.id,
        ...this.convertFirestoreData(doc.data())
      }))

      callback(notifications)
    })
  }

  /**
   * Get notification history for a user
   */
  subscribeToUserNotifications(userId: string, callback: (notifications: DeliveryNotification[]) => void): () => void {
    const q = query(
      collection(db, this.COLLECTION_NAME),
      where('recipient', '==', userId),
      orderBy('createdAt', 'desc')
    )

    return onSnapshot(q,
      (snapshot) => {
        const notifications: DeliveryNotification[] = snapshot.docs.map(doc => ({
          id: doc.id,
          ...this.convertFirestoreData(doc.data())
        }))

        callback(notifications)
      },
      (error) => {
        // Handle permission errors silently
        if (error.code === 'permission-denied') {
          console.debug('User does not have permission to access delivery notifications')
          callback([]) // Return empty array for users without permissions
          return
        }
        console.error('Error in delivery notifications listener:', error)
      }
    )
  }

  /**
   * Process notification queue (would be called by a background service)
   */
  async processNotificationQueue(): Promise<void> {
    // This would typically be implemented as:
    // 1. Cloud Function triggered by schedule
    // 2. Background service worker
    // 3. Admin dashboard action

    console.log('Processing notification queue...')

    // Get pending notifications
    const pendingNotifications = await this.getPendingNotifications()

    for (const notification of pendingNotifications) {
      try {
        await this.sendNotification(notification)
        await this.markNotificationSent(notification.id, true)
      } catch (error) {
        console.error('Error sending notification:', error)
        await this.markNotificationSent(
          notification.id,
          false,
          error instanceof Error ? error.message : 'Unknown error'
        )
      }
    }
  }

  /**
   * Get pending notifications for processing
   */
  private async getPendingNotifications(): Promise<DeliveryNotification[]> {
    // Implementation would query pending notifications
    // For now, return empty array
    return []
  }

  /**
   * Send individual notification
   */
  private async sendNotification(notification: DeliveryNotification): Promise<void> {
    switch (notification.method) {
      case 'email':
        await this.sendEmailNotification(notification)
        break
      case 'push':
        await this.sendPushNotification(notification)
        break
      case 'sms':
        await this.sendSMSNotification(notification)
        break
    }
  }

  /**
   * Send email notification
   */
  private async sendEmailNotification(notification: DeliveryNotification): Promise<void> {
    // Implementation would use email service (SendGrid, etc.)
    console.log('Sending email notification:', notification)

    const emailContent = this.generateEmailContent(notification)

    // Simulate email sending
    await new Promise(resolve => setTimeout(resolve, 100))
  }

  /**
   * Send push notification
   */
  private async sendPushNotification(notification: DeliveryNotification): Promise<void> {
    // Implementation would use Firebase Cloud Messaging
    console.log('Sending push notification:', notification)

    const pushContent = this.generatePushContent(notification)

    // Simulate push sending
    await new Promise(resolve => setTimeout(resolve, 100))
  }

  /**
   * Send SMS notification
   */
  private async sendSMSNotification(notification: DeliveryNotification): Promise<void> {
    // Implementation would use SMS service (Twilio, etc.)
    console.log('Sending SMS notification:', notification)

    const smsContent = this.generateSMSContent(notification)

    // Simulate SMS sending
    await new Promise(resolve => setTimeout(resolve, 100))
  }

  /**
   * Generate email content based on notification type
   */
  private generateEmailContent(notification: DeliveryNotification): {
    subject: string
    body: string
  } {
    switch (notification.type) {
      case 'shipped':
        return {
          subject: '📦 Tu premio ha sido enviado - WildDraw',
          body: `
            ¡Buenas noticias! Tu premio ha sido enviado y está en camino.

            Puedes seguir el estado de tu entrega en tu panel de usuario.

            ¡Gracias por participar en WildDraw!
          `
        }

      case 'delivered':
        return {
          subject: '🎉 Tu premio ha sido entregado - WildDraw',
          body: `
            ¡Excelente! Tu premio ha sido entregado con éxito.

            Esperamos que disfrutes de tu premio. No olvides compartir tu experiencia con nosotros.

            ¡Gracias por participar en WildDraw!
          `
        }

      case 'cancelled':
        return {
          subject: '❌ Entrega cancelada - WildDraw',
          body: `
            Te informamos que la entrega de tu premio ha sido cancelada.

            Nuestro equipo se pondrá en contacto contigo para resolver esta situación.

            Disculpa las molestias.
          `
        }

      case 'delayed':
        return {
          subject: '⏰ Retraso en la entrega - WildDraw',
          body: `
            Te informamos que la entrega de tu premio se ha retrasado.

            Estamos trabajando para resolver esta situación lo antes posible.

            Te mantendremos informado sobre el estado de tu entrega.
          `
        }

      default:
        return {
          subject: '📋 Actualización de entrega - WildDraw',
          body: `
            Hay una actualización en el estado de tu entrega.

            Puedes ver los detalles en tu panel de usuario.

            ¡Gracias por participar en WildDraw!
          `
        }
    }
  }

  /**
   * Generate push notification content
   */
  private generatePushContent(notification: DeliveryNotification): {
    title: string
    body: string
  } {
    switch (notification.type) {
      case 'shipped':
        return {
          title: '📦 Premio enviado',
          body: 'Tu premio está en camino. ¡Síguelo desde tu panel!'
        }

      case 'delivered':
        return {
          title: '🎉 Premio entregado',
          body: '¡Tu premio ha llegado! Esperamos que lo disfrutes.'
        }

      case 'cancelled':
        return {
          title: '❌ Entrega cancelada',
          body: 'La entrega ha sido cancelada. Te contactaremos pronto.'
        }

      case 'delayed':
        return {
          title: '⏰ Entrega retrasada',
          body: 'Tu entrega se ha retrasado. Te mantendremos informado.'
        }

      default:
        return {
          title: '📋 Actualización de entrega',
          body: 'Hay novedades en tu entrega. Revisa tu panel.'
        }
    }
  }

  /**
   * Generate SMS content
   */
  private generateSMSContent(notification: DeliveryNotification): string {
    switch (notification.type) {
      case 'shipped':
        return 'WildDraw: Tu premio ha sido enviado 📦 Síguelo en tu panel de usuario.'

      case 'delivered':
        return 'WildDraw: ¡Tu premio ha sido entregado! 🎉 ¡Disfrútalo!'

      case 'cancelled':
        return 'WildDraw: Entrega cancelada. Te contactaremos pronto para resolver la situación.'

      case 'delayed':
        return 'WildDraw: Tu entrega se ha retrasado. Te mantendremos informado sobre el nuevo estado.'

      default:
        return 'WildDraw: Actualización en tu entrega. Revisa tu panel de usuario para más detalles.'
    }
  }

  /**
   * Convert Firestore data to JavaScript objects
   */
  private convertFirestoreData(data: any): any {
    const converted = { ...data }

    Object.keys(converted).forEach(key => {
      if (converted[key] && typeof converted[key] === 'object') {
        if (converted[key].toDate && typeof converted[key].toDate === 'function') {
          converted[key] = converted[key].toDate()
        }
      }
    })

    return converted
  }
}

export const deliveryNotificationService = new DeliveryNotificationService()

/**
 * React hook for delivery notifications
 */
export const useDeliveryNotifications = (userId?: string) => {
  const [notifications, setNotifications] = React.useState<DeliveryNotification[]>([])
  const [unreadCount, setUnreadCount] = React.useState(0)

  React.useEffect(() => {
    if (!userId) return

    const unsubscribe = deliveryNotificationService.subscribeToUserNotifications(
      userId,
      (notifications) => {
        setNotifications(notifications)
        // Count unread notifications (less than 24 hours old and not seen)
        const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)
        const unread = notifications.filter(n =>
          n.createdAt > oneDayAgo && n.status === 'sent'
        ).length
        setUnreadCount(unread)
      }
    )

    return unsubscribe
  }, [userId])

  return {
    notifications,
    unreadCount,
    hasUnread: unreadCount > 0
  }
}

// Add React import at the top for the hook
import React from 'react'